/**
 * Figure and superlative extraction, import-free so the paid-media check gate can run on Deno.
 * `anatomy.ts` re-exports both.
 */

import { normalizeForMatch } from './normalize-for-match.ts';

const UNIT_WORDS =
  'hours?|days?|minutes?|weeks?|months?|years?|customers?|clients?|reviews?|stars?|orders?|employees?|states?|countries?|locations?|people|users?|members?|seats?|calls?|leads?|sales|returns?|refunds?|servings?|calories|grams?|kg|lbs?|oz|ml|litres?|liters?|miles?|km';

/**
 * A number is only a CLAIM when it says something about the world. "5 ways to
 * X" is a description of the piece's own shape, not an assertion, and flagging
 * it would be the crying-wolf failure this module exists to avoid.
 */
export function extractQuantitativeClaims(text: string): string[] {
  const found = new Set<string>();
  const patterns = [
    /\d[\d,.]*\s?%/g,
    /[$£€]\s?\d[\d,.]*/g,
    /\b\d[\d,.]*\s?x\b/gi,
    new RegExp(`\\b\\d[\\d,.]*\\s?(?:${UNIT_WORDS})\\b`, 'gi'),
    /\b\d{4,}\b/g,
  ];
  for (const pattern of patterns) {
    for (const match of text.matchAll(pattern)) {
      // Trailing sentence punctuation is not part of the number. "$400," and
      // "$400" have to be the same claim or the evidence lookup misses.
      const cleaned = match[0].trim().replace(/[.,]+$/, '');
      if (cleaned.length > 0) found.add(cleaned);
    }
  }
  return [...found];
}

const SUPERLATIVES =
  /\b(?:#1|number one|the best|the most|the only|the fastest|the cheapest|world[- ]class|industry[- ]leading|guaranteed|proven to)\b/gi;

export function extractSuperlatives(text: string): string[] {
  return [...new Set([...text.matchAll(SUPERLATIVES)].map((m) => m[0]))];
}

const MONEY = '[$£€]\\s?\\d[\\d,.]*';

const PRICE_CUT_PATTERNS: readonly RegExp[] = [
  new RegExp(`\\b(?:now|was)\\s+${MONEY}`, 'gi'),
  new RegExp(`${MONEY}\\s+off\\b`, 'gi'),
  /\b\d[\d,.]*\s?%\s+off\b/gi,
  new RegExp(`\\bsave\\s+(?:${MONEY}|\\d[\\d,.]*\\s?%)`, 'gi'),
  /\b(?:on sale|sale price|reduced|marked down|discounted)\b/gi,
];

/**
 * Wording that says a price was cut: "Now $58", "was $80", "$10 off", "20% off", "save $10", "on sale". A bare "sale" or "discount" alone is not a cut claim.
 * A figure that matches the page does not make the cut true: "Now $58" on a product with no compare-at
 * price tells the reader the price fell when it did not. Each phrase needs the page or the ad's evidence
 * to say the same, or to carry a compare-at price.
 */
export function extractPriceCutClaims(text: string): string[] {
  const found = new Set<string>();
  for (const pattern of PRICE_CUT_PATTERNS) {
    for (const match of text.matchAll(pattern)) {
      const cleaned = match[0].trim().replace(/[.,]+$/, '');
      if (cleaned.length > 0) found.add(cleaned);
    }
  }
  return [...found];
}

/** Words a page or evidence line uses when it records the price before a cut. */
export const COMPARE_AT_CUES =
  /\b(?:compare[- ]at|regular price|original price|list price|msrp|was\s+[$£€]\s?\d)/i;

/** Numbers in a text as plain values ("$58.00" and "58" are one figure), for matching a cut claim to the page. */
export function figuresIn(text: string): Set<string> {
  const out = new Set<string>();
  for (const m of text.matchAll(/\d[\d,]*(?:\.\d+)?/g)) {
    const n = Number.parseFloat(m[0].replace(/,/g, ''));
    if (Number.isFinite(n)) out.add(String(n));
  }
  return out;
}

export interface UnsupportedPriceCut {
  readonly claim: string;
  readonly why: 'no compare-at price on file' | 'not the figure on the page';
}

/**
 * The ONE price-cut rule, used by the claims check and by the ad writer so both hold the same bar.
 * A phrase passes when the page says it verbatim, or when a compare-at price is on file and the phrase
 * is a price ("now $58", "was $80") or a saving whose figure is on the page.
 */
export function unsupportedPriceCuts(text: string, poolText: string): UnsupportedPriceCut[] {
  const pool = normalizeForMatch(poolText);
  const hasCompareAt = COMPARE_AT_CUES.test(poolText);
  const poolFigures = figuresIn(poolText);
  const out: UnsupportedPriceCut[] = [];
  for (const claim of extractPriceCutClaims(text)) {
    const needle = normalizeForMatch(claim);
    if (needle.length > 0 && pool.includes(needle)) continue;
    const figures = [...figuresIn(claim)];
    const figuresMatch = /^(?:now|was)\b/i.test(claim) || figures.every((f) => poolFigures.has(f));
    if (hasCompareAt && figuresMatch) continue;
    out.push({
      claim,
      why: hasCompareAt ? 'not the figure on the page' : 'no compare-at price on file',
    });
  }
  return out;
}
